import { unref } from 'vue'
import { i18n } from '@/i18n'

// Helper to get current locale
const getLocale = (): 'es' | 'en' => {
  const locale = unref(i18n.global.locale)
  return locale === 'es' ? 'es' : 'en'
}

// ==========================================
// Enum Labels (for UI display)
// ==========================================

export const USER_ROLE_LABELS = {
  admin: { es: 'Administrador', en: 'Administrator' },
  operator: { es: 'Operador', en: 'Operator' },
  client: { es: 'Cliente', en: 'Client' },
  member: { es: 'Miembro', en: 'Member' },
} as const

export const PRIORITY_LABELS = {
  critical: { es: 'Critico', en: 'Critical' },
  high: { es: 'Alto', en: 'High' },
  medium: { es: 'Medio', en: 'Medium' },
  low: { es: 'Bajo', en: 'Low' },
} as const

export const PROSPECT_STAGE_LABELS = {
  prospect: { es: 'Prospecto', en: 'Prospect' },
  contacted: { es: 'Contactado', en: 'Contacted' },
  proposal_sent: { es: 'Propuesta enviada', en: 'Proposal Sent' },
  negotiation: { es: 'Negociacion', en: 'Negotiation' },
  won: { es: 'Ganado', en: 'Won' },
  lost: { es: 'Perdido', en: 'Lost' },
} as const

export const PROJECT_STATUS_LABELS = {
  definition: { es: 'Definicion', en: 'Definition' },
  in_development: { es: 'En desarrollo', en: 'In Development' },
  qa: { es: 'QA', en: 'QA' },
  uat: { es: 'UAT', en: 'UAT' },
  production: { es: 'Produccion', en: 'Production' },
  maintenance: { es: 'Mantenimiento', en: 'Maintenance' },
  completed: { es: 'Completado', en: 'Completed' },
  on_hold: { es: 'En pausa', en: 'On Hold' },
} as const

export const DEFINITION_STATUS_LABELS = {
  requested: { es: 'Solicitado', en: 'Requested' },
  analyzing: { es: 'En analisis', en: 'Analyzing' },
  defining: { es: 'Definiendo', en: 'Defining' },
  costing: { es: 'Costeando', en: 'Costing' },
  approved: { es: 'Aprobado', en: 'Approved' },
  rejected: { es: 'Rechazado', en: 'Rejected' },
} as const

export const DEVELOPMENT_STATUS_LABELS = {
  backlog: { es: 'Backlog', en: 'Backlog' },
  design: { es: 'Diseno', en: 'Design' },
  development: { es: 'Desarrollo', en: 'Development' },
  code_review: { es: 'Code Review', en: 'Code Review' },
  qa: { es: 'QA', en: 'QA' },
  uat: { es: 'UAT', en: 'UAT' },
  production: { es: 'Produccion', en: 'Production' },
} as const

export const TICKET_TYPE_LABELS = {
  bug: { es: 'Bug', en: 'Bug' },
  support: { es: 'Soporte', en: 'Support' },
  inquiry: { es: 'Consulta', en: 'Inquiry' },
  enhancement: { es: 'Mejora', en: 'Enhancement' },
  quotation: { es: 'Cotizacion', en: 'Quotation' },
} as const

export const TICKET_STATUS_LABELS = {
  open: { es: 'Abierto', en: 'Open' },
  in_progress: { es: 'En progreso', en: 'In Progress' },
  waiting_client: { es: 'Esperando cliente', en: 'Waiting Client' },
  resolved: { es: 'Resuelto', en: 'Resolved' },
  closed: { es: 'Cerrado', en: 'Closed' },
} as const

export const QUOTATION_STATUS_LABELS = {
  requested: { es: 'Solicitada', en: 'Requested' },
  analyzing: { es: 'Analizando', en: 'Analyzing' },
  sent: { es: 'Enviada', en: 'Sent' },
  accepted: { es: 'Aceptada', en: 'Accepted' },
  rejected: { es: 'Rechazada', en: 'Rejected' },
  expired: { es: 'Expirada', en: 'Expired' },
} as const

export const SALES_ORDER_STATUS_LABELS = {
  draft: { es: 'Borrador', en: 'Draft' },
  sent: { es: 'Enviada', en: 'Sent' },
  accepted: { es: 'Aceptada', en: 'Accepted' },
  rejected: { es: 'Rechazada', en: 'Rejected' },
  invoiced: { es: 'Facturada', en: 'Invoiced' },
  paid: { es: 'Pagada', en: 'Paid' },
  cancelled: { es: 'Cancelada', en: 'Cancelled' },
} as const

export const APPOINTMENT_STATUS_LABELS = {
  requested: { es: 'Solicitada', en: 'Requested' },
  proposed: { es: 'Propuesta', en: 'Proposed' },
  confirmed: { es: 'Confirmada', en: 'Confirmed' },
  completed: { es: 'Completada', en: 'Completed' },
  cancelled: { es: 'Cancelada', en: 'Cancelled' },
} as const

export const APPOINTMENT_TYPE_LABELS = {
  initial_contact: { es: 'Contacto inicial', en: 'Initial Contact' },
  project_review: { es: 'Revision de proyecto', en: 'Project Review' },
  demo: { es: 'Demo', en: 'Demo' },
  support: { es: 'Soporte', en: 'Support' },
  other: { es: 'Otro', en: 'Other' },
} as const

export const RELEASE_STATUS_LABELS = {
  planned: { es: 'Planificado', en: 'Planned' },
  in_progress: { es: 'En progreso', en: 'In Progress' },
  deployed: { es: 'Desplegado', en: 'Deployed' },
  rolled_back: { es: 'Rollback', en: 'Rolled Back' },
} as const

export const ACTIVITY_TYPE_LABELS = {
  call: { es: 'Llamada', en: 'Call' },
  email: { es: 'Email', en: 'Email' },
  meeting: { es: 'Reunion', en: 'Meeting' },
  note: { es: 'Nota', en: 'Note' },
} as const

export const PROJECT_TYPE_LABELS = {
  web_app: { es: 'Aplicacion Web', en: 'Web Application' },
  mobile_app: { es: 'Aplicacion Movil', en: 'Mobile Application' },
  api: { es: 'API', en: 'API' },
  ecommerce: { es: 'E-commerce', en: 'E-commerce' },
  landing: { es: 'Landing Page', en: 'Landing Page' },
  research: { es: 'Investigacion', en: 'Research' },
  infrastructure: { es: 'Infraestructura', en: 'Infrastructure' },
  commercial: { es: 'Comercial', en: 'Commercial' },
  consulting: { es: 'Consultoria', en: 'Consulting' },
  maintenance: { es: 'Mantenimiento', en: 'Maintenance' },
  marketing: { es: 'Marketing', en: 'Marketing' },
  operations: { es: 'Operaciones', en: 'Operations' },
  education: { es: 'Educacion', en: 'Education' },
  general: { es: 'General', en: 'General' },
  other: { es: 'Otro', en: 'Other' },
} as const

export const DOCUMENT_TYPE_LABELS = {
  scope: { es: 'Alcance', en: 'Scope' },
  architecture: { es: 'Arquitectura', en: 'Architecture' },
  specifications: { es: 'Especificaciones', en: 'Specifications' },
  manual: { es: 'Manual', en: 'Manual' },
  other: { es: 'Otro', en: 'Other' },
} as const

export const REQUIREMENT_TYPE_LABELS = {
  story: { es: 'Historia de Usuario', en: 'User Story' },
  task: { es: 'Tarea', en: 'Task' },
  bug: { es: 'Bug', en: 'Bug' },
  spike: { es: 'Spike', en: 'Spike' },
  subtask: { es: 'Subtarea', en: 'Subtask' },
} as const

export const REQUIREMENT_TYPE_COLORS: Record<string, string> = {
  story: 'bg-blue-500',
  task: 'bg-green-500',
  bug: 'bg-red-500',
  spike: 'bg-purple-500',
  subtask: 'bg-gray-500',
}

export const EPIC_STATUS_LABELS = {
  draft: { es: 'Borrador', en: 'Draft' },
  active: { es: 'Activa', en: 'Active' },
  completed: { es: 'Completada', en: 'Completed' },
  cancelled: { es: 'Cancelada', en: 'Cancelled' },
} as const

export const APPLICATION_PLATFORM_LABELS = {
  web: { es: 'Web', en: 'Web' },
  mobile_ios: { es: 'iOS', en: 'iOS' },
  mobile_android: { es: 'Android', en: 'Android' },
  desktop: { es: 'Desktop', en: 'Desktop' },
  api: { es: 'API', en: 'API' },
  microservice: { es: 'Microservicio', en: 'Microservice' },
  library: { es: 'Libreria', en: 'Library' },
  other: { es: 'Otro', en: 'Other' },
} as const

export const APPLICATION_STATUS_LABELS = {
  planning: { es: 'Planificacion', en: 'Planning' },
  in_development: { es: 'En desarrollo', en: 'In Development' },
  staging: { es: 'Staging', en: 'Staging' },
  production: { es: 'Produccion', en: 'Production' },
  deprecated: { es: 'Deprecado', en: 'Deprecated' },
  retired: { es: 'Retirado', en: 'Retired' },
} as const

export const APPLICATION_STATUS_COLORS = {
  planning: 'secondary',
  in_development: 'default',
  staging: 'warning',
  production: 'success',
  deprecated: 'destructive',
  retired: 'secondary',
} as const

// ==========================================
// Meeting Labels
// ==========================================

export const MEETING_TYPE_LABELS = {
  standup: { es: 'Daily/Standup', en: 'Daily/Standup' },
  sprint_planning: { es: 'Sprint Planning', en: 'Sprint Planning' },
  sprint_review: { es: 'Sprint Review', en: 'Sprint Review' },
  retrospective: { es: 'Retrospectiva', en: 'Retrospective' },
  kickoff: { es: 'Kickoff', en: 'Kickoff' },
  progress_review: { es: 'Revision de Progreso', en: 'Progress Review' },
  technical_review: { es: 'Revision Tecnica', en: 'Technical Review' },
  demo: { es: 'Demo', en: 'Demo' },
  general: { es: 'General', en: 'General' },
} as const

export const MEETING_STATUS_LABELS = {
  scheduled: { es: 'Programada', en: 'Scheduled' },
  in_progress: { es: 'En curso', en: 'In Progress' },
  completed: { es: 'Completada', en: 'Completed' },
  cancelled: { es: 'Cancelada', en: 'Cancelled' },
  postponed: { es: 'Aplazada', en: 'Postponed' },
} as const

export const MEETING_STATUS_COLORS = {
  scheduled: 'default',
  in_progress: 'warning',
  completed: 'success',
  cancelled: 'destructive',
  postponed: 'secondary',
} as const

export const RSVP_STATUS_LABELS = {
  pending: { es: 'Pendiente', en: 'Pending' },
  accepted: { es: 'Aceptada', en: 'Accepted' },
  declined: { es: 'Rechazada', en: 'Declined' },
  tentative: { es: 'Tentativa', en: 'Tentative' },
} as const

export const RSVP_STATUS_COLORS = {
  pending: 'secondary',
  accepted: 'success',
  declined: 'destructive',
  tentative: 'warning',
} as const

export const ATTENDEE_ROLE_LABELS = {
  organizer: { es: 'Organizador', en: 'Organizer' },
  required: { es: 'Requerido', en: 'Required' },
  optional: { es: 'Opcional', en: 'Optional' },
} as const

export const ACTION_ITEM_STATUS_LABELS = {
  pending: { es: 'Pendiente', en: 'Pending' },
  in_progress: { es: 'En progreso', en: 'In Progress' },
  completed: { es: 'Completado', en: 'Completed' },
} as const

export const VERSION_ENTRY_TYPE_LABELS = {
  release: { es: 'Release', en: 'Release' },
  'pre-release': { es: 'Pre-release', en: 'Pre-release' },
  hotfix: { es: 'Hotfix', en: 'Hotfix' },
  patch: { es: 'Patch', en: 'Patch' },
} as const

// ==========================================
// Branch Labels
// ==========================================

export const BRANCH_TYPE_LABELS = {
  feature: { es: 'Feature', en: 'Feature' },
  fix: { es: 'Fix', en: 'Fix' },
  hotfix: { es: 'Hotfix', en: 'Hotfix' },
  spike: { es: 'Spike', en: 'Spike' },
  release: { es: 'Release', en: 'Release' },
  chore: { es: 'Chore', en: 'Chore' },
} as const

export const BRANCH_STATUS_LABELS = {
  active: { es: 'Activa', en: 'Active' },
  pr_open: { es: 'PR Abierto', en: 'PR Open' },
  merged: { es: 'Mergeada', en: 'Merged' },
  deleted: { es: 'Eliminada', en: 'Deleted' },
  stale: { es: 'Inactiva', en: 'Stale' },
} as const

export const BRANCH_STATUS_COLORS: Record<string, string> = {
  active: 'default',
  pr_open: 'secondary',
  merged: 'default',
  deleted: 'outline',
  stale: 'destructive',
}

// ==========================================
// Priority Colors
// ==========================================

export const PRIORITY_COLORS = {
  critical: 'destructive',
  high: 'warning',
  medium: 'default',
  low: 'secondary',
} as const

// ==========================================
// Status Colors
// ==========================================

export const PROJECT_STATUS_COLORS = {
  definition: 'secondary',
  in_development: 'default',
  qa: 'warning',
  uat: 'warning',
  production: 'success',
  maintenance: 'default',
  completed: 'success',
  on_hold: 'secondary',
} as const

export const TICKET_STATUS_COLORS = {
  open: 'destructive',
  in_progress: 'warning',
  waiting_client: 'secondary',
  resolved: 'success',
  closed: 'secondary',
} as const

// ==========================================
// Helper Functions to get labels
// ==========================================

export function getRoleLabel(role: string): string {
  const labels = USER_ROLE_LABELS[role as keyof typeof USER_ROLE_LABELS]
  return labels ? labels[getLocale()] : role
}

export function getPriorityLabel(priority: string): string {
  const labels = PRIORITY_LABELS[priority as keyof typeof PRIORITY_LABELS]
  return labels ? labels[getLocale()] : priority
}

export function getProspectStageLabel(stage: string): string {
  const labels = PROSPECT_STAGE_LABELS[stage as keyof typeof PROSPECT_STAGE_LABELS]
  return labels ? labels[getLocale()] : stage
}

export function getProjectStatusLabel(status: string): string {
  const labels = PROJECT_STATUS_LABELS[status as keyof typeof PROJECT_STATUS_LABELS]
  return labels ? labels[getLocale()] : status
}

export function getDefinitionStatusLabel(status: string): string {
  const labels = DEFINITION_STATUS_LABELS[status as keyof typeof DEFINITION_STATUS_LABELS]
  return labels ? labels[getLocale()] : status
}

export function getDevelopmentStatusLabel(status: string): string {
  const labels = DEVELOPMENT_STATUS_LABELS[status as keyof typeof DEVELOPMENT_STATUS_LABELS]
  return labels ? labels[getLocale()] : status
}

export function getTicketTypeLabel(type: string): string {
  const labels = TICKET_TYPE_LABELS[type as keyof typeof TICKET_TYPE_LABELS]
  return labels ? labels[getLocale()] : type
}

export function getTicketStatusLabel(status: string): string {
  const labels = TICKET_STATUS_LABELS[status as keyof typeof TICKET_STATUS_LABELS]
  return labels ? labels[getLocale()] : status
}

export function getQuotationStatusLabel(status: string): string {
  const labels = QUOTATION_STATUS_LABELS[status as keyof typeof QUOTATION_STATUS_LABELS]
  return labels ? labels[getLocale()] : status
}

export function getSalesOrderStatusLabel(status: string): string {
  const labels = SALES_ORDER_STATUS_LABELS[status as keyof typeof SALES_ORDER_STATUS_LABELS]
  return labels ? labels[getLocale()] : status
}

export function getAppointmentStatusLabel(status: string): string {
  const labels = APPOINTMENT_STATUS_LABELS[status as keyof typeof APPOINTMENT_STATUS_LABELS]
  return labels ? labels[getLocale()] : status
}

export function getAppointmentTypeLabel(type: string): string {
  const labels = APPOINTMENT_TYPE_LABELS[type as keyof typeof APPOINTMENT_TYPE_LABELS]
  return labels ? labels[getLocale()] : type
}

export function getReleaseStatusLabel(status: string): string {
  const labels = RELEASE_STATUS_LABELS[status as keyof typeof RELEASE_STATUS_LABELS]
  return labels ? labels[getLocale()] : status
}

export function getActivityTypeLabel(type: string): string {
  const labels = ACTIVITY_TYPE_LABELS[type as keyof typeof ACTIVITY_TYPE_LABELS]
  return labels ? labels[getLocale()] : type
}

export function getProjectTypeLabel(type: string): string {
  const labels = PROJECT_TYPE_LABELS[type as keyof typeof PROJECT_TYPE_LABELS]
  return labels ? labels[getLocale()] : type
}

export function getDocumentTypeLabel(type: string): string {
  const labels = DOCUMENT_TYPE_LABELS[type as keyof typeof DOCUMENT_TYPE_LABELS]
  return labels ? labels[getLocale()] : type
}

export function getRequirementTypeLabel(type: string): string {
  const labels = REQUIREMENT_TYPE_LABELS[type as keyof typeof REQUIREMENT_TYPE_LABELS]
  return labels ? labels[getLocale()] : type
}

export function getApplicationPlatformLabel(platform: string): string {
  const labels = APPLICATION_PLATFORM_LABELS[platform as keyof typeof APPLICATION_PLATFORM_LABELS]
  return labels ? labels[getLocale()] : platform
}

export function getApplicationStatusLabel(status: string): string {
  const labels = APPLICATION_STATUS_LABELS[status as keyof typeof APPLICATION_STATUS_LABELS]
  return labels ? labels[getLocale()] : status
}

export function getVersionEntryTypeLabel(type: string): string {
  const labels = VERSION_ENTRY_TYPE_LABELS[type as keyof typeof VERSION_ENTRY_TYPE_LABELS]
  return labels ? labels[getLocale()] : type
}

export function getBranchTypeLabel(type: string): string {
  const labels = BRANCH_TYPE_LABELS[type as keyof typeof BRANCH_TYPE_LABELS]
  return labels ? labels[getLocale()] : type
}

export function getBranchStatusLabel(status: string): string {
  const labels = BRANCH_STATUS_LABELS[status as keyof typeof BRANCH_STATUS_LABELS]
  return labels ? labels[getLocale()] : status
}

export function getMeetingTypeLabel(type: string): string {
  const labels = MEETING_TYPE_LABELS[type as keyof typeof MEETING_TYPE_LABELS]
  return labels ? labels[getLocale()] : type
}

export function getMeetingStatusLabel(status: string): string {
  const labels = MEETING_STATUS_LABELS[status as keyof typeof MEETING_STATUS_LABELS]
  return labels ? labels[getLocale()] : status
}

export function getRsvpStatusLabel(status: string): string {
  const labels = RSVP_STATUS_LABELS[status as keyof typeof RSVP_STATUS_LABELS]
  return labels ? labels[getLocale()] : status
}

export function getAttendeeRoleLabel(role: string): string {
  const labels = ATTENDEE_ROLE_LABELS[role as keyof typeof ATTENDEE_ROLE_LABELS]
  return labels ? labels[getLocale()] : role
}

export function getActionItemStatusLabel(status: string): string {
  const labels = ACTION_ITEM_STATUS_LABELS[status as keyof typeof ACTION_ITEM_STATUS_LABELS]
  return labels ? labels[getLocale()] : status
}

// ==========================================
// Sprint Labels
// ==========================================

export const SPRINT_STATUS_LABELS = {
  planning: { es: 'Planificacion', en: 'Planning' },
  active: { es: 'Activo', en: 'Active' },
  completed: { es: 'Completado', en: 'Completed' },
  cancelled: { es: 'Cancelado', en: 'Cancelled' },
} as const

export const SPRINT_STATUS_COLORS = {
  planning: 'secondary',
  active: 'default',
  completed: 'success',
  cancelled: 'destructive',
} as const

export function getSprintStatusLabel(status: string): string {
  const labels = SPRINT_STATUS_LABELS[status as keyof typeof SPRINT_STATUS_LABELS]
  return labels ? labels[getLocale()] : status
}

// ==========================================
// Blog Labels
// ==========================================

export const BLOG_POST_STATUS_LABELS = {
  draft: { es: 'Borrador', en: 'Draft' },
  published: { es: 'Publicado', en: 'Published' },
  archived: { es: 'Archivado', en: 'Archived' },
} as const

export const BLOG_POST_STATUS_COLORS: Record<string, string> = {
  draft: 'secondary',
  published: 'success',
  archived: 'outline',
}

export const BLOG_SECTION_TYPE_LABELS = {
  markdown: { es: 'Markdown', en: 'Markdown' },
  image: { es: 'Imagen', en: 'Image' },
  video: { es: 'Video', en: 'Video' },
  quote: { es: 'Cita', en: 'Quote' },
  code: { es: 'Codigo', en: 'Code' },
  gallery: { es: 'Galeria', en: 'Gallery' },
  references: { es: 'Referencias', en: 'References' },
  callout: { es: 'Destacado', en: 'Callout' },
  divider: { es: 'Separador', en: 'Divider' },
} as const

export function getBlogPostStatusLabel(status: string): string {
  const labels = BLOG_POST_STATUS_LABELS[status as keyof typeof BLOG_POST_STATUS_LABELS]
  return labels ? labels[getLocale()] : status
}

export function getBlogSectionTypeLabel(type: string): string {
  const labels = BLOG_SECTION_TYPE_LABELS[type as keyof typeof BLOG_SECTION_TYPE_LABELS]
  return labels ? labels[getLocale()] : type
}

// ==========================================
// Task Status
// ==========================================

export const TASK_STATUS_LABELS = {
  backlog: { es: 'Backlog', en: 'Backlog' },
  todo: { es: 'Por hacer', en: 'To Do' },
  in_progress: { es: 'En progreso', en: 'In Progress' },
  review: { es: 'En revision', en: 'In Review' },
  done: { es: 'Completada', en: 'Done' },
  blocked: { es: 'Bloqueada', en: 'Blocked' },
  cancelled: { es: 'Cancelada', en: 'Cancelled' },
} as const

export const TASK_STATUS_COLORS: Record<string, string> = {
  backlog: 'bg-gray-500',
  todo: 'bg-blue-500',
  in_progress: 'bg-yellow-500',
  review: 'bg-purple-500',
  done: 'bg-green-500',
  blocked: 'bg-red-500',
  cancelled: 'bg-gray-400',
}

export function getTaskStatusLabel(status: string): string {
  const labels = TASK_STATUS_LABELS[status as keyof typeof TASK_STATUS_LABELS]
  return labels ? labels[getLocale()] : status
}

// ==========================================
// Task Type
// ==========================================

export const TASK_TYPE_LABELS = {
  feature: { es: 'Funcionalidad', en: 'Feature' },
  bug: { es: 'Bug', en: 'Bug' },
  improvement: { es: 'Mejora', en: 'Improvement' },
  documentation: { es: 'Documentacion', en: 'Documentation' },
  chore: { es: 'Tarea', en: 'Chore' },
  research: { es: 'Investigacion', en: 'Research' },
} as const

export const TASK_TYPE_COLORS: Record<string, string> = {
  feature: 'bg-blue-500',
  bug: 'bg-red-500',
  improvement: 'bg-green-500',
  documentation: 'bg-orange-500',
  chore: 'bg-gray-500',
  research: 'bg-purple-500',
}

export function getTaskTypeLabel(type: string): string {
  const labels = TASK_TYPE_LABELS[type as keyof typeof TASK_TYPE_LABELS]
  return labels ? labels[getLocale()] : type
}

// ==========================================
// Deliverable Labels
// ==========================================

export const DELIVERABLE_TYPE_LABELS = {
  service: { es: 'Servicio', en: 'Service' },
  infrastructure: { es: 'Infraestructura', en: 'Infrastructure' },
  research: { es: 'Investigacion', en: 'Research' },
  component: { es: 'Componente', en: 'Component' },
  hardware: { es: 'Hardware', en: 'Hardware' },
  other: { es: 'Otro', en: 'Other' },
} as const

export const DELIVERABLE_STATUS_LABELS = {
  planning: { es: 'Planificacion', en: 'Planning' },
  active: { es: 'Activo', en: 'Active' },
  in_progress: { es: 'En progreso', en: 'In Progress' },
  on_hold: { es: 'En pausa', en: 'On Hold' },
  completed: { es: 'Completado', en: 'Completed' },
  cancelled: { es: 'Cancelado', en: 'Cancelled' },
  retired: { es: 'Retirado', en: 'Retired' },
} as const

export const DELIVERABLE_STATUS_COLORS = {
  planning: 'secondary',
  active: 'default',
  in_progress: 'warning',
  on_hold: 'secondary',
  completed: 'success',
  cancelled: 'destructive',
  retired: 'secondary',
} as const

export const DELIVERABLE_SERVICE_TYPE_LABELS = {
  maintenance: { es: 'Mantenimiento', en: 'Maintenance' },
  consulting: { es: 'Consultoria', en: 'Consulting' },
  support: { es: 'Soporte', en: 'Support' },
  managed: { es: 'Gestionado', en: 'Managed' },
  training: { es: 'Capacitacion', en: 'Training' },
  other: { es: 'Otro', en: 'Other' },
} as const

export const DELIVERABLE_ENVIRONMENT_LABELS = {
  cloud: { es: 'Cloud', en: 'Cloud' },
  on_premise: { es: 'On Premise', en: 'On Premise' },
  hybrid: { es: 'Hibrido', en: 'Hybrid' },
  other: { es: 'Otro', en: 'Other' },
} as const

export const DELIVERABLE_RECURRENCE_LABELS = {
  one_time: { es: 'Una vez', en: 'One Time' },
  weekly: { es: 'Semanal', en: 'Weekly' },
  monthly: { es: 'Mensual', en: 'Monthly' },
  quarterly: { es: 'Trimestral', en: 'Quarterly' },
  yearly: { es: 'Anual', en: 'Yearly' },
} as const

export function getDeliverableTypeLabel(type: string): string {
  const labels = DELIVERABLE_TYPE_LABELS[type as keyof typeof DELIVERABLE_TYPE_LABELS]
  return labels ? labels[getLocale()] : type
}

export function getDeliverableStatusLabel(status: string): string {
  const labels = DELIVERABLE_STATUS_LABELS[status as keyof typeof DELIVERABLE_STATUS_LABELS]
  return labels ? labels[getLocale()] : status
}

export function getDeliverableStatusColor(status: string): string {
  return DELIVERABLE_STATUS_COLORS[status as keyof typeof DELIVERABLE_STATUS_COLORS] || 'secondary'
}

export function getDeliverableServiceTypeLabel(type: string): string {
  const labels = DELIVERABLE_SERVICE_TYPE_LABELS[type as keyof typeof DELIVERABLE_SERVICE_TYPE_LABELS]
  return labels ? labels[getLocale()] : type
}

export function getDeliverableEnvironmentLabel(env: string): string {
  const labels = DELIVERABLE_ENVIRONMENT_LABELS[env as keyof typeof DELIVERABLE_ENVIRONMENT_LABELS]
  return labels ? labels[getLocale()] : env
}

export function getDeliverableRecurrenceLabel(rec: string): string {
  const labels = DELIVERABLE_RECURRENCE_LABELS[rec as keyof typeof DELIVERABLE_RECURRENCE_LABELS]
  return labels ? labels[getLocale()] : rec
}

// ==========================================
// Implementation Labels
// ==========================================

export const IMPLEMENTATION_STATUS_LABELS = {
  planning: { es: 'Planificación', en: 'Planning' },
  configuring: { es: 'Configuración', en: 'Configuring' },
  data_migration: { es: 'Migración de Datos', en: 'Data Migration' },
  uat: { es: 'UAT', en: 'UAT' },
  training: { es: 'Capacitación', en: 'Training' },
  go_live: { es: 'Go-Live', en: 'Go-Live' },
  hypercare: { es: 'Hypercare', en: 'Hypercare' },
  completed: { es: 'Completado', en: 'Completed' },
  suspended: { es: 'Suspendido', en: 'Suspended' },
  cancelled: { es: 'Cancelado', en: 'Cancelled' },
} as const

export const IMPLEMENTATION_STATUS_COLORS: Record<string, string> = {
  planning: 'secondary',
  configuring: 'default',
  data_migration: 'warning',
  uat: 'warning',
  training: 'default',
  go_live: 'success',
  hypercare: 'success',
  completed: 'success',
  suspended: 'secondary',
  cancelled: 'destructive',
}

export const IMPLEMENTATION_TYPE_LABELS = {
  new_deployment: { es: 'Nueva Instalación', en: 'New Deployment' },
  migration: { es: 'Migración', en: 'Migration' },
  upgrade: { es: 'Actualización', en: 'Upgrade' },
  integration: { es: 'Integración', en: 'Integration' },
} as const

export function getImplementationStatusLabel(status: string): string {
  const labels = IMPLEMENTATION_STATUS_LABELS[status as keyof typeof IMPLEMENTATION_STATUS_LABELS]
  return labels ? labels[getLocale()] : status
}

export function getImplementationTypeLabel(type: string): string {
  const labels = IMPLEMENTATION_TYPE_LABELS[type as keyof typeof IMPLEMENTATION_TYPE_LABELS]
  return labels ? labels[getLocale()] : type
}
